/**
 * DataFast client helpers — goals, identify, Stripe revenue attribution.
 *
 * Script: index.html → https://datafa.st/js/script.js (public site only;
 * /admin /dashboard /portal skip load for performance).
 * Docs: https://datafa.st/docs/custom-goals
 *       https://datafa.st/docs/user-identification
 *       https://datafa.st/docs/stripe-checkout-api
 *       https://datafa.st/docs/conversion-funnels
 *
 * Stripe-only revenue (Connect). MercadoPago is out of scope for DataFast.
 *
 * Reserved goal names (payment provider — do NOT send as custom goals):
 * payment, free_trial, subscription_*, trial_*
 *
 * Brand: every goal merges BRAND_SOURCE_KEY so MRR/funnel is attributable
 * to this creator (multi-brand isolation).
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { isAdminShellPath } from "@/lib/geoConsentRoutes";

const VISITOR_KEY = "datafast_visitor_id";
const SESSION_KEY = "datafast_session_id";

function isDataFastDisabled(): boolean {
  if (typeof window === "undefined") return true;
  if ((window as unknown as { __PUBLIC_ANALYTICS_DISABLED__?: boolean }).__PUBLIC_ANALYTICS_DISABLED__) {
    return true;
  }
  try {
    return isAdminShellPath(window.location.pathname || "/");
  } catch {
    return false;
  }
}

/** Reserved by DataFast payment integration — never fire as custom goals. */
const RESERVED_GOAL_NAMES = new Set([
  "payment",
  "free_trial",
  "trial_started",
  "trial_converted",
  "subscription_started",
  "subscription_upgraded",
  "subscription_downgraded",
  "subscription_renewed",
  "subscription_cancel_scheduled",
  "subscription_reactivated",
  "subscription_ended",
  "identify", // reserved for identify API / method name
]);

export type DataFastIds = {
  datafast_visitor_id: string | null;
  datafast_session_id: string | null;
};

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  try {
    const parts = document.cookie.split(";");
    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed.startsWith(`${name}=`)) continue;
      const value = decodeURIComponent(trimmed.slice(name.length + 1).trim());
      return value || null;
    }
  } catch {
    /* private mode / blocked */
  }
  return null;
}

function readSessionStorage(name: string): string | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const value = sessionStorage.getItem(name)?.trim();
    return value || null;
  } catch {
    return null;
  }
}

function readLocalStorage(name: string): string | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const value = localStorage.getItem(name)?.trim();
    return value || null;
  } catch {
    return null;
  }
}

function readDataFastId(name: string): string | null {
  return readCookie(name) || readSessionStorage(name) || readLocalStorage(name);
}

export function getDataFastIds(): DataFastIds {
  return {
    datafast_visitor_id: readDataFastId(VISITOR_KEY),
    datafast_session_id: readDataFastId(SESSION_KEY),
  };
}

/**
 * Flat Stripe metadata keys for revenue attribution (Methods 1–2).
 * Includes brand key so Connect revenue is known to be this brand's MRR.
 * @see https://datafa.st/docs/stripe-checkout-api
 */
export function getDataFastStripeMetadata(): Record<string, string> {
  const ids = getDataFastIds();
  const out: Record<string, string> = {};
  if (ids.datafast_visitor_id) {
    out.datafast_visitor_id = ids.datafast_visitor_id.slice(0, 200);
  }
  if (ids.datafast_session_id) {
    out.datafast_session_id = ids.datafast_session_id.slice(0, 200);
  }
  const brand = (BRAND_SOURCE_KEY || "").trim().toLowerCase();
  if (brand) {
    // Keep in Stripe metadata alongside checkout `source` for brand-scoped MRR
    out.brand_source_key = brand.slice(0, 50);
  }
  return out;
}

/** Brand props for DataFast goals (max 10 total with caller params). */
function getDataFastBrandGoalParams(): Record<string, string> {
  const brand = (BRAND_SOURCE_KEY || "").trim().toLowerCase();
  if (!brand) return {};
  return { brand: brand.slice(0, 64), source: brand.slice(0, 64) };
}

function sanitizeGoalName(name: string): string | null {
  const cleaned = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.:-]/g, "_")
    .slice(0, 64);
  return cleaned || null;
}

function sanitizeParams(
  params?: Record<string, string | number | boolean | null | undefined>,
): Record<string, string> | undefined {
  if (!params || typeof params !== "object") return undefined;
  const payload: Record<string, string> = {};
  let count = 0;
  for (const [rawKey, rawVal] of Object.entries(params)) {
    if (count >= 10) break;
    if (rawVal === undefined || rawVal === null) continue;
    const key = rawKey
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "_")
      .slice(0, 64);
    if (!key) continue;
    payload[key] = String(rawVal).slice(0, 255);
    count++;
  }
  return Object.keys(payload).length > 0 ? payload : undefined;
}

/**
 * Track a DataFast custom goal (queued if script not loaded yet).
 * @see https://datafa.st/docs/custom-goals
 */
export function trackDataFastGoal(
  goalName: string,
  params?: Record<string, string | number | boolean | null | undefined>,
): void {
  if (typeof window === "undefined" || isDataFastDisabled()) return;
  const name = sanitizeGoalName(goalName);
  if (!name || RESERVED_GOAL_NAMES.has(name)) return;

  // Brand first so BRAND_SOURCE_KEY always survives the 10-param cap (MRR isolation).
  // Caller may override brand/source if intentionally set.
  const merged: Record<string, string | number | boolean | null | undefined> = {
    ...getDataFastBrandGoalParams(),
    ...(params || {}),
  };
  const payload = sanitizeParams(merged);

  try {
    if (typeof window.datafast === "function") {
      if (payload) window.datafast(name, payload);
      else window.datafast(name);
    }
  } catch {
    /* tracker blocked or not ready */
  }
}

/**
 * Identify a known user (Clerk / email). Call on each session when user is available.
 * @see https://datafa.st/docs/user-identification
 */
export function identifyDataFastUser(opts: {
  userId: string;
  name?: string | null;
  image?: string | null;
  email?: string | null;
  plan?: string | null;
  extra?: Record<string, string | number | boolean | null | undefined>;
}): void {
  if (typeof window === "undefined" || isDataFastDisabled()) return;
  const userId = opts.userId?.trim();
  if (!userId) return;

  const payload: Record<string, string> = {
    user_id: userId.slice(0, 255),
  };
  if (opts.name?.trim()) payload.name = opts.name.trim().slice(0, 255);
  if (opts.image?.trim()) payload.image = opts.image.trim().slice(0, 250);
  if (opts.email?.trim()) payload.email = opts.email.trim().toLowerCase().slice(0, 255);
  if (opts.plan?.trim()) payload.plan = opts.plan.trim().slice(0, 64);

  if (opts.extra) {
    const extra = sanitizeParams(opts.extra);
    if (extra) {
      let count = Object.keys(payload).length;
      for (const [k, v] of Object.entries(extra)) {
        if (count >= 10) break;
        if (k === "user_id") continue;
        payload[k] = v;
        count++;
      }
    }
  }

  try {
    if (typeof window.datafast === "function") {
      window.datafast("identify", payload);
    }
  } catch {
    /* tracker blocked */
  }
}

/**
 * Funnel-friendly goals for Dual CTA / membership (client-side).
 * Pair with CLI: datafast funnels create …
 */
export const DATAFAST_GOALS = {
  dualCtaView: "dual_cta_view",
  vipCtaImpression: "vip_cta_impression",
  vipCtaClick: "vip_cta_click",
  freeCtaClick: "free_cta_click",
  videoStarted: "video_started",
  videoEnded: "video_ended",
  initiateCheckout: "initiate_checkout",
  signup: "signup",
  registroStarted: "registro_started",
  checkoutSuccessPage: "checkout_success_page",
  muxCtaClick: "mux_cta_click",
} as const;
